import { useEffect, useState } from 'react';
import { isOnline } from '../lib/network';
import { OFFLINE, type NetStatus } from '../lib/networkStatus';

/** The current value, as one of the two shapes `NetStatus` permits. */
function read(): NetStatus {
  return isOnline() ? { online: true } : OFFLINE;
}

/**
 * Live connectivity for the UI — the counterpart of Android's
 * `NetworkInfo.observe()`, which emits a `NetworkStatus` on every connectivity
 * change from a `ConnectivityManager` callback.
 *
 * Only the UI. The cycle does **not** read this hook: `runner.ts` gates itself
 * with `awaitOnline`, because a React state update that arrives one render later
 * is no basis for deciding whether 200 links are dead. This is what draws the
 * offline banner and greys out the Start button, and nothing more.
 *
 * The seed comes from `isOnline()` rather than from `navigator.onLine`
 * directly, so the whole app reads that property through one place and inherits
 * its one-directional interpretation: `false` is trustworthy, `true` only means
 * an interface exists.
 *
 * Three subscriptions rather than the obvious two:
 *
 * - `online` / `offline` are the events proper;
 * - `pageshow` and `visibilitychange` re-read the value on the way back to the
 *   foreground, because this app spends most of its life suspended (SPEC §9).
 *   iOS freezes the page when the screen locks or Safari goes to the background,
 *   and events that fire while it is frozen are never delivered — so a device
 *   that lost wifi in a pocket would come back still claiming to be online, and
 *   the banner that is supposed to explain a stalled cycle would be missing.
 *
 * The state holds its previous object when the value has not changed. Consumers
 * pass this straight into effect dependency lists; a fresh `{ online: true }` on
 * every `visibilitychange` would restart their effects for no reason.
 */
export function useNetworkStatus(): NetStatus {
  const [status, setStatus] = useState<NetStatus>(read);

  useEffect(() => {
    const sync = () => setStatus((prev) => (prev.online === isOnline() ? prev : read()));
    window.addEventListener('online', sync);
    window.addEventListener('offline', sync);
    window.addEventListener('pageshow', sync);
    document.addEventListener('visibilitychange', sync);
    // Catch a change that happened between the first render and this effect —
    // those events had nobody listening.
    sync();
    return () => {
      window.removeEventListener('online', sync);
      window.removeEventListener('offline', sync);
      window.removeEventListener('pageshow', sync);
      document.removeEventListener('visibilitychange', sync);
    };
  }, []);

  return status;
}
